/* eslint-disable no-param-reassign */
import { useMemo } from 'react';
import _ from 'underscore';
import * as Highcharts from 'highcharts';
import { merge } from 'lodash';
import { tokens } from '@fluentui/react-components';
import { formatTooltip } from './formatter/format-tooltip';
import { useChartData } from './use-chart-data';
import { staticChartStyleOption } from './static-options';
import { gridLineStyle } from '../styles/line';
import {
  IPerformanceTimeSeries,
  ISeries,
  MetricTypes,
  TAxisLabelsFormatterContextObject,
  TAxisOptions,
} from '../types/types';
import { style } from '../styles/chart';

function getLabel(value, series: ISeries) {
  return series.data[value]?.[0];
}

function getFluctuateIndex(chartSeries) {
  const dataPoints = chartSeries.data;
  const fluctuateDate = dataPoints?.find((item) => item[2]);
  const index = dataPoints?.indexOf(fluctuateDate);

  return { start: index - 0.5, end: index + 0.5 };
}

function formatXAxisLabel({
  value,
  series,
}) {
  const label = getLabel(value, series);
  return label && `<span class="highcharts-el_label highcharts-el__label">${label}</span>`;
}

const chart = {
  style,
};

export function useChartViewOptions({
  i18n,
  metricType,
  performanceTimeSeries,
  TimeZone,
}: {
  i18n: any,
  metricType: MetricTypes,
  performanceTimeSeries: IPerformanceTimeSeries,
  TimeZone: number,
}) {
  const chartSeries: Partial<ISeries> = useChartData({
    performanceTimeSeries,
    i18n,
    metricType,
    TimeZone,
  });
  const isAllValuesZero = useMemo(() => _.every(chartSeries?.data || [], (point) => point[1] === 0), [chartSeries]);
  const xAxis: TAxisOptions = useMemo(() => ({
    labels: {
      enabled: true,
      formatter(this: TAxisLabelsFormatterContextObject) {
        return formatXAxisLabel({
          value: this.value,
          series: chartSeries,
        });
      },
      style: {
        color: tokens.colorNeutralForeground3Hover,
      },
      useHTML: true,
    },
    showFirstLabel: true,
    showLastLabel: true,
    minPadding: 0.03,
    maxPadding: 0.03,
    startOnTick: false,
    endOnTick: false,
    series: {
      marker: {
        enabled: false,
      },
    },
    plotBands: [{
      from: getFluctuateIndex(chartSeries).start,
      to: getFluctuateIndex(chartSeries).end,
    }],
  }), [chartSeries]);

  const chartStyleOption = useMemo(() => {
    const x = staticChartStyleOption.xAxis;
    let y = staticChartStyleOption.yAxis;
    if (isAllValuesZero) {
      y = {
        ...y,
        min: 0,
        max: 1,
        tickInterval: 0.5,
      };
    }

    return {
      ...staticChartStyleOption,
      xAxis: x,
      yAxis: y,
    };
  }, [isAllValuesZero]);

  const highchartsOptions = useMemo(() => {
    const yAxis = {
      labels: {
        formatter(this: TAxisLabelsFormatterContextObject) {
          return i18n.formatDecimal(this.value);
        },
      },
      ...gridLineStyle,
    };

    const tooltip = {
      formatter(this: { points: any[] }) {
        return formatTooltip({
          points: this.points,
          i18n,
        });
      },
    };

    return {
      series: _.isEmpty(chartSeries) ? [] : [chartSeries],
      tooltip,
      xAxis,
      yAxis,
      chart,
    };
  }, [chartSeries, xAxis]);

  const result = useMemo(() => ({
    highcharts: Highcharts,
    options: merge({}, chartStyleOption, highchartsOptions),
    hasChartData: !_.isEmpty(highchartsOptions?.series[0]),
  }), [chartStyleOption, highchartsOptions]);

  return result;
}
